import React from 'react';

const WIRES = [
  { d: 'M20 78 C 80 78, 115 66, 150 42', tone: 'rose', layer: 2 },
  { d: 'M20 100 C 110 100, 150 152, 230 152 S 305 132, 336 132', tone: 'teal2', layer: 1, end: [336, 132] },
  { d: 'M20 120 C 100 120, 140 72, 220 72 L 336 72', tone: 'teal', layer: 3, end: [336, 72] },
  { d: 'M20 140 C 120 140, 150 196, 240 196 L 336 196', tone: 'teal', layer: 2, end: [336, 196] },
  { d: 'M20 160 C 110 160, 150 112, 230 112 S 300 92, 336 92', tone: 'teal2', layer: 3, end: [336, 92] },
  { d: 'M20 182 C 100 182, 130 128, 180 88 S 232 34, 246 24', tone: 'rose', layer: 1 },
  { d: 'M34 202 C 110 202, 168 146, 214 108 S 262 64, 276 54', tone: 'rose', layer: 2 },
  { d: 'M20 222 C 110 222, 160 168, 252 168 L 300 168', tone: 'teal2', layer: 1, end: [300, 168] },
  { d: 'M20 240 C 110 240, 150 226, 214 226 L 268 226', tone: 'teal', layer: 3, end: [268, 226] },
  { d: 'M20 200 L 52 200', tone: 'rose', layer: 2, end: [52, 200] }
];

const WIFI = [
  { x: 156, y: 36, tone: 'rose', scale: 0.85, layer: 2 },
  { x: 300, y: 36, tone: 'teal2', scale: 1, layer: 3 },
  { x: 312, y: 150, tone: 'teal2', scale: 0.8, layer: 1 },
  { x: 298, y: 232, tone: 'teal', scale: 1, layer: 3 }
];

const arc = (r) => {
  const k = 0.72;
  return `M ${-r * k} ${-r * k} A ${r} ${r} 0 0 1 ${r * k} ${-r * k}`;
};

const Wire = ({ d, tone, end, index }) => (
  <g className={`lx-wire lx-wire--${tone}`}>
    <path className="lx-wire-body" d={d} />
    <path className="lx-wire-hl" d={d} />
    <path
      className="lx-wire-flow"
      d={d}
      style={{ animationDelay: `${(index % 5) * -0.9}s` }}
    />
    {end && (
      <circle className="lx-wire-end" cx={end[0]} cy={end[1]} r="5.5" fill={`url(#lx-dot-${tone})`} />
    )}
  </g>
);

const Wifi = ({ x, y, tone, scale }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <g className={`lx-wifi lx-wire--${tone}`}>
      {[9, 16, 23].map((r, i) => (
        <g key={r} className="lx-wifi-arc" style={{ animationDelay: `${i * 0.25}s` }}>
          <path className="lx-wire-body" d={arc(r)} />
          <path className="lx-wire-hl" d={arc(r)} />
        </g>
      ))}
      <circle r="4.5" fill={`url(#lx-dot-${tone})`} className="lx-wire-end" />
    </g>
  </g>
);

const DotGradient = ({ id, tone }) => (
  <radialGradient id={id} cx="35%" cy="30%" r="75%">
    <stop offset="0%" style={{ stopColor: 'var(--lx-wire-hl)' }} />
    <stop offset="45%" style={{ stopColor: `var(--lx-wire-${tone})` }} />
    <stop offset="100%" style={{ stopColor: `var(--lx-wire-${tone}-deep)` }} />
  </radialGradient>
);

const LoginIllustration3D = () => (
  <svg
    className="lx-illustration"
    viewBox="0 0 360 260"
    role="img"
    aria-label="Ilustração de conexões de rede"
  >
    <defs>
      <DotGradient id="lx-dot-teal" tone="teal" />
      <DotGradient id="lx-dot-teal2" tone="teal2" />
      <DotGradient id="lx-dot-rose" tone="rose" />
      <filter id="lx-relief" x="-10%" y="-10%" width="120%" height="130%">
        <feDropShadow dx="1.5" dy="3" stdDeviation="2" style={{ floodColor: 'var(--lx-wire-shadow)' }} />
      </filter>
    </defs>

    {[1, 2, 3].map((layer) => (
      <g key={layer} className={`lx-layer lx-layer--${layer}`} filter="url(#lx-relief)">
        {WIRES.map((w, i) =>
          w.layer === layer ? <Wire key={i} index={i} {...w} /> : null
        )}
        {WIFI.map((w, i) => (w.layer === layer ? <Wifi key={`w${i}`} {...w} /> : null))}
      </g>
    ))}
  </svg>
);

const LEAF_PATH = 'M0 0 C 7 -7, 19 -8, 28 0 C 19 6, 7 6, 0 0 Z';

const LEAVES = [
  [128, 214, -150, 1], [121, 196, -30, 1], [110, 176, -158, 0.95], [104, 158, -24, 0.95],
  [93, 138, -152, 0.9], [87, 120, -28, 0.9], [77, 100, -150, 0.82], [70, 82, -32, 0.82],
  [58, 62, -146, 0.72], [51, 46, -36, 0.7], [40, 30, -140, 0.6], [30, 16, -60, 0.55]
];

export const LoginLeaves = () => (
  <svg className="lx-leaves" viewBox="0 0 160 260" aria-hidden="true">
    <path className="lx-leaves-stem" d="M150 258 C 125 210, 100 160, 72 96 S 40 30, 26 10" />
    {LEAVES.map(([x, y, rot, s], i) => (
      <g key={i} transform={`translate(${x} ${y}) rotate(${rot}) scale(${s})`}>
        <path className="lx-leaf" d={LEAF_PATH} style={{ animationDelay: `${i * -0.5}s` }} />
      </g>
    ))}
  </svg>
);

export const LoginVase = () => <div className="lx-vase" aria-hidden="true" />;

export default LoginIllustration3D;
